import { memo, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MenuOutlined, MoreOutlined } from '@ant-design/icons'
import { Button, Popover } from 'antd'
import clsx from 'clsx'
import Skeleton from 'react-loading-skeleton'

import A11yButton from '@/components/A11yButton'
import ConfirmModal from '@/components/ConfirmModal'
import ImageWithFallBack from '@/components/ImageWithFallback'
import EditWishlistModal from '@/components/Wishlist/EditWishlistModal'
import PermissionWrapper from '@/HOCs/permissionWrapper'
import { MODULE_WISHLIST_RIGHTS } from '@/libs/constants/modulerights'

import type { Wishlist } from './types'
import styles from './WishlistItem.module.scss'

export const WishlistItemSkeleton = () => {
  return (
    <div className={styles.wishlistItem}>
      <div style={{ marginBottom: 23, padding: '0 24px' }}>
        <Skeleton width="100%" height={68} />
      </div>
      <div className={styles.productList}>
        <div className={styles.productItemSkeleton}>
          <Skeleton height="100%" />
        </div>
        <div className={styles.productItemSkeleton}>
          <Skeleton height="100%" />
        </div>
        <div className={styles.productItemSkeleton}>
          <Skeleton height="100%" />
        </div>
        <div className={styles.productItemSkeleton}>
          <Skeleton height="100%" />
        </div>
        <div className={styles.productItemSkeleton}>
          <Skeleton height="100%" />
        </div>
        <div className={styles.productItemSkeleton}>
          <Skeleton height="100%" />
        </div>
      </div>
    </div>
  )
}

interface WishlistItemProps extends Wishlist {
  setShouldShowModal?: (value: boolean) => void
  disableSharing?: boolean
  isDragging?: boolean
  dragHandleProps?: React.HTMLAttributes<HTMLElement>
  onCardClick: () => void
  onRename: (name: string) => void
  onDelete: (id: string) => void
}

const WishlistItem = (props: WishlistItemProps) => {
  const {
    wishlistName,
    wishlistId,
    ownerId,
    isShared,
    ownerEmail,
    productCount,
    productPictures,
    onCardClick,
    disableSharing = false,
    isDragging = false,
    dragHandleProps,
    onRename,
    onDelete,
  } = props
  const { t } = useTranslation()

  const [isDisplayDeleteModal, setIsDisplayDeleteModal] = useState<boolean>(false)
  const [shouldDisplayEditModal, setShouldDisplayEditModal] = useState<boolean>(false)
  const [isPopoverOptionsVisible, setIsPopoverOptionsVisible] = useState<boolean>(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleEditWishlist = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      setIsPopoverOptionsVisible(false)
      setShouldDisplayEditModal(true)
      setIsDisplayDeleteModal(false)
    },
    [],
  )

  const handleDeleteWishlist = useCallback(() => {
    if (wishlistId && ownerId) {
      onDelete(wishlistId)
    }
  }, [wishlistId, ownerId, onDelete])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setIsPopoverOptionsVisible(false)
    setIsDisplayDeleteModal(true)
  }, [])

  const handleOnMouseLeave = useCallback(() => {
    setIsPopoverOptionsVisible(false)
    if (buttonRef.current?.blur) {
      buttonRef.current.blur()
    }
  }, [])

  const handleVisibleChange = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setIsPopoverOptionsVisible(true)
  }

  return (
    <>
      <A11yButton
        elementType="div"
        className={clsx(styles.wishlistItem, isDragging && styles.isDragging)}
        onClick={onCardClick}
        onMouseLeave={handleOnMouseLeave}
      >
        <div className={styles.header}>
          {dragHandleProps && (
            <div
              className={styles.dragHandle}
              {...dragHandleProps}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuOutlined />
            </div>
          )}
          <div className={styles.wishlistItemInfo}>
            <div>
              <h3>{wishlistName}</h3>
              {isShared && (
                <>
                  <span className={styles.infoText}>
                    {t('wishlist.shared')}
                  </span>
                  {ownerEmail && (
                    <span className={styles.infoText}>
                      {`${t('wishlist.owner')}: ${ownerEmail}`}
                    </span>
                  )}
                </>
              )}
              <span className={styles.infoText}>
                {t('wishlist.productCount', { count: productCount })}
              </span>
            </div>
          </div>

          {!isShared && (
            <Popover
              open={isPopoverOptionsVisible}
              classNames={{
                root: styles.overlay,
              }}
              placement="bottomRight"
              trigger={['click', 'hover']}
              content={
                <A11yButton
                  elementType="div"
                  className={styles.wishlistActions}
                  onMouseLeave={handleOnMouseLeave}
                  onClick={handleOnMouseLeave}
                >
                  <ul>
                    <PermissionWrapper
                      permission={MODULE_WISHLIST_RIGHTS.EDIT_SHARE_WISHLIST}
                    >
                      <A11yButton
                        elementType="li"
                        className={styles.action}
                        onClick={handleEditWishlist}
                      >
                        {t('wishlist.actions.edit')}
                      </A11yButton>
                      {!disableSharing && (
                        <A11yButton
                          elementType="li"
                          className={styles.action}
                          onClick={handleEditWishlist}
                        >
                          {t('wishlist.actions.share')}
                        </A11yButton>
                      )}
                    </PermissionWrapper>
                    <PermissionWrapper
                      permission={MODULE_WISHLIST_RIGHTS.DELETE_WISHLIST}
                    >
                      <A11yButton
                        elementType="li"
                        className={styles.action}
                        onClick={handleDelete}
                      >
                        {t('wishlist.actions.delete')}
                      </A11yButton>
                    </PermissionWrapper>
                  </ul>
                </A11yButton>
              }
            >
              <Button
                ref={buttonRef}
                icon={<MoreOutlined />}
                shape="circle"
                size="large"
                onClick={handleVisibleChange}
              />
            </Popover>
          )}
        </div>
        <div>
          <div className={styles.productList}>
            {productPictures
              .filter((img) => !!img)
              .map((img) => {
                return (
                  <div key={img} className={styles.productItem}>
                    <ImageWithFallBack
                      src={img}
                      alt=""
                      height={98}
                      width={98}
                    />
                  </div>
                )
              })}
          </div>
        </div>
      </A11yButton>

      <ConfirmModal
        confirmActionHandler={handleDeleteWishlist}
        isShowModal={isDisplayDeleteModal}
        modalContent={t('wishlist.modal.deleteWishlist.modalContent')}
        modalTitle={t('wishlist.modal.deleteWishlist.title')}
        setIsShowModal={setIsDisplayDeleteModal}
      />

      {shouldDisplayEditModal && (
        <EditWishlistModal
          ownerId={ownerId}
          wishlistId={wishlistId}
          shouldShowModal={shouldDisplayEditModal}
          shouldShowSharing={!disableSharing}
          setShouldShowModal={setShouldDisplayEditModal}
          onRenameWishlist={onRename}
          wishlistName={wishlistName}
        />
      )}
    </>
  )
}

export default memo(WishlistItem)
